import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { API_BASE_URL } from '../config';
const TickerTape = ({
  stocks = [],
  speed = 'medium', // 'slow', 'medium', 'fast'
  height = '50px',
  showVolume = false,
  showChange = true,
  backgroundColor = 'rgba(0, 0, 0, 0.9)',
  textColor = '#00ff41'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);

  // Speed configurations (pixels per second)
  const speedConfig = {
    slow: 30,
    medium: 50,
    fast: 80
  };

  // Calculate animation duration based on content width and speed
  const getAnimationDuration = () => {
    if (!tickerRef.current) return '60s';
    const contentWidth = tickerRef.current.scrollWidth;
    const pixelsPerSecond = speedConfig[speed] || speedConfig.medium;
    const duration = (contentWidth + window.innerWidth) / pixelsPerSecond;
    return `${duration}s`;
  };

  // Format stock data for display
  const formatStockDisplay = (stock) => {
    const change = stock.change || 0;
    const changePercent = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : 0;

    let display = `${stock.ticker} ${formatCurrency(stock.currentPrice)}`;

    if (showChange) {
      const changeSymbol = change >= 0 ? '▲' : '▼';
      const changeColor = change >= 0 ? '#00ff88' : '#ff4444';
      display += ` ${changeSymbol}${formatPercent(Math.abs(changePercent))}`;
    }

    if (showVolume && stock.volume) {
      display += ` Vol: ${stock.volume.toLocaleString()}`;
    }

    return {
      text: display,
      color: showChange ? (change >= 0 ? '#00ff88' : change < 0 ? '#ff4444' : textColor) : textColor,
      stock
    };
  };

  // Create ticker content with separators
  const createTickerContent = () => {
    if (!stocks || stocks.length === 0) {
      return [
        { text: 'MARKET OPENING SOON...', color: textColor },
        { text: '●', color: textColor },
        { text: 'STOCK MARKET PARTY', color: '#00bcd4' },
        { text: '●', color: textColor },
        { text: 'TRADE LIVE STOCKS', color: textColor },
        { text: '●', color: textColor }
      ];
    }

    const formattedStocks = stocks.map(formatStockDisplay);
    const content = [];

    formattedStocks.forEach((stock, index) => {
      content.push(stock);
      if (index < formattedStocks.length - 1) {
        content.push({ text: '●', color: 'rgba(0, 255, 65, 0.5)' });
      }
    });

    return content;
  };

  const tickerContent = createTickerContent();

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === ' ' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        setIsPaused(!isPaused);
      }
      if (e.key === 'h' || e.key === 'H') {
        setIsVisible(!isVisible);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPaused, isVisible]);

  if (!isVisible) return null;

  const animationDuration = getAnimationDuration();

  return (
    <div
      className="ticker-tape-container"
      style={{
        width: '100%',
        height,
        backgroundColor,
        borderTop: '1px solid #00ff41',
        borderBottom: '1px solid #00ff41',
        overflow: 'hidden',
        position: 'relative',
        whiteSpace: 'nowrap',
        fontFamily: '"Courier New", monospace',
        fontSize: '14px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
      }}
      onClick={() => setIsPaused(!isPaused)}
      title="Click to pause/resume • Press SPACE to pause • Press H to hide"
    >
      {/* Gradient fade edges */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '50px',
          height: '100%',
          background: `linear-gradient(90deg, ${backgroundColor} 0%, transparent 100%)`,
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: '50px',
          height: '100%',
          background: `linear-gradient(90deg, transparent 0%, ${backgroundColor} 100%)`,
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />

      {/* Scrolling content */}
      <div
        ref={tickerRef}
        className="ticker-content"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '30px',
          animation: `scroll-left ${animationDuration} linear infinite`,
          animationPlayState: isPaused ? 'paused' : 'running',
          willChange: 'transform'
        }}
      >
        {/* Duplicate content for seamless loop */}
        {[...Array(3)].map((_, loopIndex) => (
          <div key={loopIndex} style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            {tickerContent.map((item, index) => (
              <span
                key={`${loopIndex}-${index}`}
                style={{
                  color: item.color,
                  textShadow: '0 0 5px currentColor',
                  transition: 'color 0.3s ease',
                  fontSize: item.text === '●' ? '8px' : '14px'
                }}
                className="ticker-item"
              >
                {item.text}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* Controls indicator */}
      {isPaused && (
        <div
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#ffa500',
            fontSize: '12px',
            zIndex: 3,
            animation: 'blink 1s infinite'
          }}
        >
          PAUSED
        </div>
      )}

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(100vw);
          }
          100% {
            transform: translateX(-100%);
          }
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }

        .ticker-tape-container:hover .ticker-item {
          animation: glow 0.3s ease;
        }

        @keyframes glow {
          0% { text-shadow: 0 0 5px currentColor; }
          50% { text-shadow: 0 0 15px currentColor, 0 0 25px currentColor; }
          100% { text-shadow: 0 0 5px currentColor; }
        }
      `}</style>
    </div>
  );
};

// Enhanced TickerTape with market data integration
const LiveTickerTape = ({
  speed = 'medium',
  height = '50px',
  showVolume = false,
  showMarketStatus = true
}) => {
  const [stocks, setStocks] = useState([]);
  const [marketStatus, setMarketStatus] = useState('LOADING');
  const [lastUpdate, setLastUpdate] = useState(null);

  // Fetch stock data
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        // Fetch market status
        try {
          const statusResponse = await fetch(`${API_BASE_URL}/api/market/status`);
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setMarketStatus(statusData.status || 'OPEN');
          }
        } catch (statusError) {
          // Fallback to time-based logic
          const currentHour = new Date().getHours();
          setMarketStatus((currentHour >= 6 && currentHour <= 23) ? 'OPEN' : 'CLOSED');
        }

        // Fetch stocks
        const response = await fetch(`${API_BASE_URL}/api/stocks`);
        if (response.ok) {
          const stockData = await response.json();
          setStocks(stockData);
          setLastUpdate(new Date());
        }
      } catch (error) {
        console.error('Error fetching stocks for ticker:', error);
        setMarketStatus('ERROR');
      }
    };

    // Initial fetch
    fetchStocks();

    // Set up periodic updates every 10 seconds
    const interval = setInterval(fetchStocks, 10000);

    return () => clearInterval(interval);
  }, []);

  // Add market status to ticker content
  const enhancedStocks = [...stocks];

  if (showMarketStatus) {
    const statusColor = {
      'OPEN': '#00ff88',
      'CLOSED': '#ff4444',
      'LOADING': '#ffa500',
      'ERROR': '#ff4444'
    }[marketStatus];

    enhancedStocks.unshift({
      ticker: `MARKET ${marketStatus}`,
      currentPrice: lastUpdate ? lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--',
      change: 0,
      basePrice: 1
    });
  }

  return (
    <TickerTape
      stocks={enhancedStocks}
      speed={speed}
      height={height}
      showVolume={showVolume}
      showChange={true}
    />
  );
};

// Export both components
export default TickerTape;
export { LiveTickerTape };